import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Content,
  DatePicker,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Pagination,
  SearchInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, SearchIcon, SyncAltIcon, WalletIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useAdminWallets } from '../../../hooks/useAdminWallets';
import { formatDateNumeric, formatDateTime } from '../../../utils/dateFormat';
import { formatPriceAmount } from '../../../utils/money';
import { BalanceAmount } from '../../Organization/Billing/paymentDisplay';
import { parseStrictIsoDate, sumMoney, toApiDate } from './walletUtils';

const PER_PAGE_DEFAULT = 25;

/**
 * Every prepaid wallet with its client (GET /admin/wallets). One call returns the whole set, so
 * search and pagination are client-side (Mode A). The optional "Balance as of" date asks the backend
 * for end-of-day balances at that cut-off — the deposit liability at a reporting date.
 */
const WalletsListTab: React.FunctionComponent<{ isStaff: boolean }> = ({ isStaff }) => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [atInput, setAtInput] = useState('');
  const [at, setAt] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_DEFAULT);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchValue), 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  // A DatePicker fires onChange on every keystroke; only a complete, real date is committed (and an
  // emptied input clears the cut-off) — debounced so a half-typed date never hits the API.
  const atCandidate = toApiDate(atInput);
  useEffect(() => {
    const handle = setTimeout(() => setAt(atInput === '' ? undefined : atCandidate), 400);
    return () => clearTimeout(handle);
  }, [atInput, atCandidate]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, at]);

  const { wallets, at: atEcho, loading, error, refetch } = useAdminWallets(at, isStaff);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (q === '') {
      return wallets;
    }
    return wallets.filter((w) => w.client_name.toLowerCase().includes(q));
  }, [wallets, debouncedSearch]);

  const paginated = useMemo(() => filtered.slice((page - 1) * perPage, page * perPage), [filtered, page, perPage]);
  const totalLiability = useMemo(() => sumMoney(filtered.map((w) => w.balance)), [filtered]);

  const hasLoadedOnce = wallets.length > 0 || !loading;

  if (loading && !hasLoadedOnce) {
    return <CenteredSpinner />;
  }

  if (error && wallets.length === 0) {
    return (
      <EmptyState
        titleText="Unable to load the wallets"
        icon={ExclamationCircleIcon}
        status="danger"
        headingLevel="h2"
        variant="lg"
      >
        <EmptyStateBody>{error}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" onClick={() => void refetch()}>
              Retry
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  if (!loading && wallets.length === 0 && debouncedSearch === '' && at === undefined) {
    return (
      <EmptyState titleText="No wallets yet" icon={WalletIcon} headingLevel="h2" variant="lg">
        <EmptyStateBody>A wallet appears here as soon as a customer starts their first top-up.</EmptyStateBody>
      </EmptyState>
    );
  }

  const balanceHeader = atEcho ? `Balance on ${formatDateNumeric(atEcho)}` : 'Balance';

  const pagination = (variant?: 'bottom') => (
    <Pagination
      itemCount={filtered.length}
      perPage={perPage}
      page={page}
      onSetPage={(_e, p) => setPage(p)}
      onPerPageSelect={(_e, pp) => {
        setPerPage(pp);
        setPage(1);
      }}
      variant={variant}
    />
  );

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by organization..."
              value={searchValue}
              onChange={(_e, v) => setSearchValue(v)}
              onClear={() => setSearchValue('')}
            />
          </ToolbarItem>
          <ToolbarItem>
            <DatePicker
              value={atInput}
              onChange={(_e, v) => setAtInput(v)}
              dateParse={parseStrictIsoDate}
              aria-label="Balance as of date"
              buttonAriaLabel="Toggle balance-as-of calendar"
              placeholder="Balance as of (YYYY-MM-DD)"
            />
          </ToolbarItem>
          <ToolbarItem>
            <Button
              variant="secondary"
              icon={<SyncAltIcon />}
              onClick={() => void refetch()}
              isLoading={loading}
              isDisabled={loading}
            >
              Refresh
            </Button>
          </ToolbarItem>
          <ToolbarItem variant="pagination">{pagination()}</ToolbarItem>
        </ToolbarContent>
      </Toolbar>

      {error && (
        <Content
          component="p"
          className="pf-v6-u-mb-sm"
          style={{ color: 'var(--pf-t--global--text--color--status--danger--default)' }}
        >
          {error}
        </Content>
      )}

      {filtered.length === 0 ? (
        <EmptyState titleText="No wallets match your search" icon={SearchIcon} headingLevel="h2" variant="lg">
          <EmptyStateBody>No organization name matches your current search.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => setSearchValue('')}>
                Clear filter
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      ) : (
        <>
          <Content component="p" className="pf-v6-u-mb-sm">
            <strong>Total liability{atEcho ? ` on ${formatDateNumeric(atEcho)}` : ''}:</strong>{' '}
            {formatPriceAmount(totalLiability)} across {filtered.length} wallet{filtered.length === 1 ? '' : 's'}
          </Content>
          <Table aria-label="Prepaid wallets" variant="compact">
            <Thead>
              <Tr>
                <Th>Organization</Th>
                <Th className="pf-v6-u-text-align-end">{balanceHeader}</Th>
                <Th>Currency</Th>
                <Th>Last movement</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginated.map((w) => (
                <Tr key={w.client_id} isClickable onRowClick={() => navigate(`/admin/wallets/${w.client_id}`)}>
                  <Td dataLabel="Organization">
                    <Link to={`/admin/wallets/${w.client_id}`} onClick={(e) => e.stopPropagation()}>
                      {w.client_name}
                    </Link>
                  </Td>
                  <Td dataLabel={balanceHeader} className="pf-v6-u-text-align-end">
                    <BalanceAmount value={w.balance} />
                  </Td>
                  <Td dataLabel="Currency">{w.currency}</Td>
                  <Td dataLabel="Last movement">{formatDateTime(w.updated_at)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          {pagination('bottom')}
        </>
      )}
    </>
  );
};

export default WalletsListTab;
